import { useRef, useEffect } from 'react';
import { useDraggable } from '@dnd-kit/core';
import clsx from 'clsx';
import { useOptionalBookmarkSelection } from '../../../contexts/BookmarkSelectionContext';
import { useOptionalBookmarkExitAnimation } from '../../../contexts/BookmarkExitAnimationContext';
import { useOptionalBookmarkDnd } from '../../../contexts/BookmarkDndContext';
import { isMobileDevice } from '../../../utils/platformUtils';
import BookmarkRow from './BookmarkRow';
import type { Bookmark, BookmarkActionViewMode } from '../../../types/api';
import type { BookmarkDragData } from '../../../contexts/BookmarkDndContext';

interface DraggableBookmarkRowProps extends Bookmark {
  condensedView?: boolean;
  showThumbnails?: boolean;
  viewMode?: BookmarkActionViewMode;
  currentFolderId?: string;
  profileMode?: boolean;
}

function DraggableBookmarkRow(props: DraggableBookmarkRowProps) {
  const { id, title, url, ...restProps } = props;
  const selectionContext = useOptionalBookmarkSelection();
  const exitAnimation = useOptionalBookmarkExitAnimation();
  const dndContext = useOptionalBookmarkDnd();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const isExiting = exitAnimation?.exitingBookmarkIds.has(id) ?? false;

  useEffect(() => {
    if (!isExiting || !wrapperRef.current) return;

    const element = wrapperRef.current;
    const handleTransitionEnd = (e: TransitionEvent) => {
      // only respond to our own opacity transition
      if (e.target === element && e.propertyName === 'opacity') {
        exitAnimation?.onExitComplete(id);
      }
    };

    element.addEventListener('transitionend', handleTransitionEnd);
    return () => element.removeEventListener('transitionend', handleTransitionEnd);
  }, [isExiting, id, exitAnimation]);

  const selectedIds = selectionContext?.selectedBookmarkIds ?? new Set<string>();
  const isSelected = selectedIds.has(String(id));
  const bookmarkIds = isSelected ? Array.from(selectedIds) : [id];

  const dragData: BookmarkDragData = {
    type: 'bookmark',
    bookmarkIds,
    singleBookmarkId: id,
    bookmarkTitle: title,
  };

  const isDragDisabled = isMobileDevice();

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `bookmark-${id}`,
    data: dragData,
    disabled: isDragDisabled,
  });

  const isBeingDragged =
    isDragging || (dndContext?.isDragging && dndContext.draggedBookmarkIds.includes(id));

  const combinedRef = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    (wrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
  };

  // dnd-kit's draggable attributes include role="button",
  // aria-pressed, and aria-roledescription="sortable",
  // which makes this wrapper an interactive control containing the
  // (interactive) BookmarkRow — a nested-interactive violation. Drop those
  // attrs so the row inside is the real interactive surface, and
  // aria-describedby still routes SR users to dnd-kit's instructions.
  const {
    role: _ignoredRole,
    tabIndex: _ignoredTabIndex,
    'aria-pressed': _ignoredAriaPressed,
    'aria-roledescription': _ignoredAriaRoleDescription,
    ...dragAttrs
  } = attributes;

  return (
    <div
      ref={combinedRef}
      data-bookmark-id={id}
      className={clsx(
        'bookmark-drag-wrapper overflow-hidden transition-all duration-200 outline-none',
        isExiting && 'max-h-0 opacity-0',
        !isExiting && 'max-h-[200px]',
        isBeingDragged && 'opacity-65',
        !isDragDisabled && 'cursor-grab',
        isDragging && 'cursor-grabbing',
      )}
      {...dragAttrs}
      {...listeners}
      tabIndex={-1}
    >
      <BookmarkRow id={id} title={title} url={url} {...restProps} />
    </div>
  );
}

export default DraggableBookmarkRow;
